/**
 * The one place behaviour events leave the browser (ENGINEERING_RULES 22: the network, the page
 * lifecycle and `sendBeacon` live behind one module). Callers push small events as things
 * happen; they are sent together every few seconds, and what is left goes out when the page is
 * hidden, with `sendBeacon`, which the browser finishes even as the tab closes.
 *
 * Best effort by design: a batch that does not arrive is not retried. An analytics event is never
 * worth a request the person can feel, a duplicate, or a queue that grows while she is offline.
 * Nothing here is React state, so a stream of events can never re-render the page.
 */

export type SinkEvent = {
	kind: string
	data: Record<string, unknown>
	/** Ids of the things this is about; the server keeps only the ones that are hers. */
	refs?: Record<string, string>
	/** The teacher's visit (lib/visitId.ts); a student's page sends none. */
	visit?: string
}
type Queued = SinkEvent & { at: number }

/** What the api accepts in one request. */
const MAX_BATCH = 40
const FLUSH_MS = 5000
/** A page that never gets to send drops its oldest, rather than growing without bound. */
const MAX_QUEUE = 200
/** The api clamps anything older to this; saying so here keeps the two honest. */
const MAX_AGE_MS = 6 * 60 * 60 * 1000

export type EventSink = {
	push: (event: SinkEvent) => void
	/** Sends what is queued now; resolves when the requests are answered. `leaving`: the page is going away, use `sendBeacon`. */
	flush: (leaving?: boolean) => Promise<void>
	/** Sends on page hide while it is listening; returns the function that stops. */
	listen: () => () => void
}

/** `endpoint` is read at send time; null (no attempt yet) keeps the queue for later. */
export function createEventSink(endpoint: () => string | null): EventSink {
	let queue: Queued[] = []
	let timer: ReturnType<typeof setTimeout> | undefined

	const flush = (leaving = false): Promise<void> => {
		clearTimeout(timer)
		timer = undefined
		const url = endpoint()
		if (!url) return Promise.resolve()
		const sent: Promise<unknown>[] = []
		while (queue.length > 0) {
			const batch = queue.splice(0, MAX_BATCH)
			const now = Date.now()
			const body = JSON.stringify({
				events: batch.map(({ at, ...e }) => ({
					...e,
					ageMs: Math.max(0, Math.min(now - at, MAX_AGE_MS)),
				})),
			})
			if (leaving && navigator.sendBeacon?.(url, new Blob([body], { type: 'application/json' })))
				continue
			sent.push(
				fetch(url, {
					method: 'POST',
					headers: { 'content-type': 'application/json' },
					credentials: 'include',
					keepalive: leaving,
					body,
				}).catch(() => {}),
			)
		}
		return Promise.all(sent).then(() => {})
	}

	return {
		push: (event) => {
			if (queue.length >= MAX_QUEUE) queue.shift()
			queue.push({ ...event, at: Date.now() })
			timer ??= setTimeout(() => flush(), FLUSH_MS)
		},
		flush,
		listen: () => {
			const onVisibility = () => {
				if (document.visibilityState === 'hidden') flush(true)
			}
			const onLeave = () => flush(true)
			document.addEventListener('visibilitychange', onVisibility)
			window.addEventListener('pagehide', onLeave)
			return () => {
				document.removeEventListener('visibilitychange', onVisibility)
				window.removeEventListener('pagehide', onLeave)
			}
		},
	}
}
